import React, { FC, useContext, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Button
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  DeleteTechnologyResponse,
  GetTechnololgyByIdResponse,
  Technology,
  TechnologyItem,
  UpdateTechnologyResponse
} from "../../Graphql/types/technology";
import EditTechnologyDialog from "./EditTechnologyDialog";
import EditTechnologyItemDialog from "./EditTechnologyItemDialog";
import {
  deleteTechnologyQuery,
  getTechnololgyByIdQuery,
  updateTechnologyQuery
} from "../../Graphql/querries/technologyQuery";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import WithAuth from "../../Hoc/WithAuth";
import { TECHNOLOGIES } from "../Routes";

const TechnologyDetail: FC = () => {
  // Extract the 'id' from the URL parameters
  const { technologyId } = useParams<{ technologyId: string }>();
  const [tech, setTech] = useState<Technology | undefined>();
  const [openEditTech, setOpenEditTech] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [openEditItem, setOpenEditItem] = useState(false);
  const { setAlert } = useContext(AlertBannerContext);
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation<
    GetTechnololgyByIdResponse,
    Error,
    string
  >({
    mutationKey: ["getTechnologyDetail"],
    mutationFn: (id) => fetchGraphQL(getTechnololgyByIdQuery, { id }, setAlert),
    onSuccess: (response) => {
      setTech(response.getTechnologyById);
    }
  });

  const { mutate: updateTech, isPending: updatingTech } = useMutation<
    UpdateTechnologyResponse,
    Error,
    Technology
  >({
    mutationKey: ["updateTechnologyDetail"],
    mutationFn: (input) =>
      fetchGraphQL(updateTechnologyQuery, { input }, setAlert),
    onSuccess: (response) => {
      setTech(response.updateTechnology);

      // close model
      setOpenEditItem(false);
      setOpenEditTech(false);
      setSelectedItemIndex(-1);
    },
    onError: () => {
      if (technologyId) mutate(technologyId);
    }
  });

  const { mutate: deleteTechnology, isPending: deletingTech } = useMutation<
    DeleteTechnologyResponse,
    Error,
    string
  >({
    mutationKey: ["deleteTechnology"],
    mutationFn: (id) => fetchGraphQL(deleteTechnologyQuery, { id }, setAlert),
    onSuccess: () => {
      navigate(TECHNOLOGIES);
    }
  });

  const handleDeleteTech = () => {
    if (tech) deleteTechnology(tech.id);
  };

  const handleEditTech = (updatedTech: Technology) => {
    updateTech(updatedTech);
  };

  const handleEditItem = (updatedItem: TechnologyItem, index: number): void => {
    if (!tech) return;

    const newTech = { ...tech };
    newTech.technologyItems[index] = updatedItem;

    updateTech(newTech);
  };

  const handleDeleteItem = (index: number) => {
    if (!tech) return;

    const newTech = { ...tech };
    newTech.technologyItems = [
      ...newTech.technologyItems.slice(0, index),
      ...newTech.technologyItems.slice(index + 1)
    ];

    updateTech(newTech);
  };

  useEffect(() => {
    if (technologyId) {
      mutate(technologyId);
    }
  }, []);

  if (isPending) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  if (!tech) {
    return <Typography>Technology doesn&apos;t exist</Typography>;
  }

  return (
    <Container maxWidth="md">
      <Stack
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(TECHNOLOGIES)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
        <Button
          startIcon={<DeleteForeverIcon />}
          onClick={() => {
            handleDeleteTech();
          }}
          sx={{ color: "red" }}
        >
          Delete
        </Button>
      </Stack>

      {/* Technology Overview */}
      <Card
        sx={{ mb: 2, p: 2, display: "flex", gap: 2, flexDirection: "column" }}
      >
        <Typography variant="h4">{tech.title}</Typography>
        <Typography variant="body1">{tech.description}</Typography>
        <Stack width="fit-content" justifyContent="right" alignSelf="end">
          <Button
            startIcon={<EditIcon />}
            onClick={() => setOpenEditTech(true)}
          >
            Edit
          </Button>
        </Stack>
      </Card>

      {(updatingTech || deletingTech) && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}

      {/* Technology Items List */}
      <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
        Technology Items
      </Typography>
      <Grid container spacing={2}>
        {tech.technologyItems.map((item, index) => (
          <Grid item xs={12} sm={6} key={item.title}>
            <Card
              sx={{
                p: 2,
                display: "flex",
                gap: 2,
                flexDirection: "column"
              }}
            >
              <CardContent>
                <Typography variant="h6">{item.title}</Typography>
                <Typography variant="body2">{item.description}</Typography>
                <a
                  href={item.detailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn More
                </a>
                <Stack
                  width="100%"
                  justifyContent="space-between"
                  flexDirection="row"
                >
                  <Button
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => {
                      handleDeleteItem(index);
                    }}
                    sx={{ color: "red" }}
                  >
                    Delete
                  </Button>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setSelectedItemIndex(index);
                      setOpenEditItem(true);
                    }}
                  >
                    Edit
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Stack width="100%" justifyContent="center" padding="2em">
          <Card
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              justifyContent: "center",
              alignSelf: "center",
              margin: "auto",
              background: "#d9f1ff"
            }}
          >
            <Button
              onClick={() => {
                setOpenEditItem(true);
                setSelectedItemIndex(tech.technologyItems.length);
              }}
              startIcon={<AddIcon />}
            >
              Add new item
            </Button>
          </Card>
        </Stack>
      </Grid>

      {/* Edit Modals */}
      {openEditTech && (
        <EditTechnologyDialog
          open={openEditTech}
          onClose={() => setOpenEditTech(false)}
          technology={tech}
          onSave={handleEditTech}
          isUpdating={updatingTech}
        />
      )}

      {openEditItem && (
        <EditTechnologyItemDialog
          open={openEditItem}
          onClose={() => {
            setOpenEditItem(false);
            setSelectedItemIndex(-1);
          }}
          technologyItemIndex={selectedItemIndex}
          onSave={handleEditItem}
          technology={{ ...tech }}
          isUpdating={updatingTech}
        />
      )}
    </Container>
  );
};

export default WithAuth(TechnologyDetail);
